import { Button, Card, Flex, Stack, Title, useMatches } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { UserProfileSidebarBox } from "../SidebarBox";

import { UserAvatar } from "@/components/elements";
import { IconUpgrade } from "@/components/icons";
import { ChoosePlanModal } from "@/components/widgets/PlanModals";
import { global_settings } from "@/config";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { useProfile } from "@/services/profile/get-profile";
import { getUserName } from "@/utils/getUserName";

import type { FC } from "react";
import type { NavigationItem } from "../../types";

interface UserProfileSidebarProps {
  activeTab?: string;
  navigationItems: NavigationItem[];
  onTabChange: (tab: string) => void;
}

export const UserProfileSidebar: FC<UserProfileSidebarProps> = ({
  activeTab,
  navigationItems,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const [showPlanModal, { open: openPlanModal, close: closePlanModal }] = useDisclosure();

  const { data: plans, isLoading: isPlanLoading } = usePlansUser();
  const isMobile = useMatches({ base: true, sm: false });
  const { data, isError } = useProfile();

  const readForcePlanToLoad = () => {
    try {
      if (typeof window === "undefined" || !window.sessionStorage) return false;
      const raw = window.sessionStorage.getItem("forcePlanToLoad");
      if (!raw) return false;
      const expiry = Number(raw);
      if (!Number.isNaN(expiry) && Date.now() < expiry) return true;
      window.sessionStorage.removeItem("forcePlanToLoad");
      return false;
    } catch {
      return false;
    }
  };

  const [forcePlanToLoad, setForcePlanToLoad] = useState<boolean>(readForcePlanToLoad);

  useEffect(() => {
    const handler = () => setForcePlanToLoad(readForcePlanToLoad());
    window.addEventListener("forcePlanToLoadChanged", handler);

    // If user returns from Stripe before the 5 s window (page reload kills the
    // original setTimeout), reschedule cleanup for the remaining time.
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const raw = window.sessionStorage.getItem("forcePlanToLoad");
      if (raw) {
        const remaining = Number(raw) - Date.now();
        if (remaining > 0) {
          timer = setTimeout(() => {
            window.sessionStorage.removeItem("forcePlanToLoad");
            setForcePlanToLoad(false);
          }, remaining);
        }
      }
    } catch {
      /* ignore */
    }

    return () => {
      window.removeEventListener("forcePlanToLoadChanged", handler);
      if (timer !== undefined) clearTimeout(timer);
    };
  }, []);

  if (!data || isError) return null;

  const name = getUserName(data);

  const content = (
    <Stack pt="lg">
      <Flex
        justify="space-between"
        align="center"
        gap="lg"
        py="md"
        px={{ base: "lg", sm: "md", md: "sm" }}
      >
        <UserAvatar size={80} user={data} variant="light" enableUpload={true} />

        <Flex direction="column" gap="xs" style={{ flex: 1, minWidth: 0 }}>
          <Flex justify="flex-start">
            {isPlanLoading || forcePlanToLoad ? (
              <Button
                component="button"
                leftSection={<IconUpgrade size={16} />}
                size="compact-sm"
                mb="sm"
                disabled
                styles={{ label: { textTransform: "none" } }}
                aria-label={t("common.loading")}
              >
                {t("common.loading")}
              </Button>
            ) : plans?.name && global_settings.enablePayments ? (
              <Button
                component="button"
                leftSection={<IconUpgrade size={16} />}
                size="compact-sm"
                mb="sm"
                onClick={() => openPlanModal()}
                styles={{ label: { textTransform: "none" } }}
                aria-label={t(`plans.type.${plans?.name ?? "starter"}`)}
              >
                {t(`plans.type.${plans?.name ?? "starter"}`)}
              </Button>
            ) : null}
          </Flex>

          <Title
            order={4}
            ta="left"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
            title={name}
          >
            {name}
          </Title>
        </Flex>
      </Flex>

      <Flex direction="column" gap="sm" style={{ width: "100%" }}>
        {/* only visible items */}
        {navigationItems
          .filter((item) => item.visible)
          .map((item) => (
            <UserProfileSidebarBox
              key={item.label}
              item={item}
              activeTab={activeTab}
              onTabChange={onTabChange}
            />
          ))}
      </Flex>

      <ChoosePlanModal
        isOpen={showPlanModal}
        onClose={() => closePlanModal()}
        title={t("plans.changePlanAnytime")}
      />
    </Stack>
  );

  return isMobile ? <Card px="sm">{content}</Card> : content;
};
